import { showNotification } from '@mantine/notifications';
import { FilterOptionType } from '@repo/common/components/Filter';
import { EntityType } from '@repo/common/enums/entityType';
import type { IconNames } from '@repo/foundations';
import { Icon, Text } from '@repo/foundations';
import { Suspense, useCallback, useMemo, useState } from 'react';
import type { Monitor } from '../../api';
import {
	useBulkUpdateMonitors,
	useDeleteMonitor,
	useRunMonitors,
	useUpdateMonitor,
} from '../../api';
import {
	closeModal as closeManagedModal,
	openModal as openManagedModal,
} from '../../components/ModalManager';
import { closeSpotlight } from '../../components/Spotlight';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';

import { monitorsQueryKeyFactory } from '../../api/hooks/monitoring/constants';
import type { ICommandListItem } from '../../components/Spotlight/components/CommandPalette/constants';
import { useDeleteManyAction } from '../../components/TableV2/BaseModel.hooks';
import { OptimizedOwnerRender } from '../../components/TableV2/OptimizedOwnerRender';
import {
	LastUpdatedRender,
	MonitorResourceRenderV2,
	TagRender,
} from '../../components/TableV2/render';
import MonitorStatusBadge from '../MonitorPage/components/MonitorStatusBadge';
import { MonitorToggle } from '../MonitorPage/MonitorToggle';
import BulkAddTagsModal from './BulkAddTagsModal';
import BulkEditOwnersModal from './BulkEditOwnersModal';
import BulkEditSchedulesModal from './BulkEditSchedulesModal';
import BulkEditThresholdsModal from './BulkEditThresholdsModal';
import IncidentStatusRender from './IncidentStatusRender';

const BULK_EDIT_OWNERS_MODAL_ID = 'bulk-edit-owners-modal';

export function useColumns<T extends Monitor>(): ExtendedDataTableColumn<T>[] {
	const { mutateAsync: updateMonitor } = useUpdateMonitor({});

	const toggleCallBack = useCallback(
		async (monitor: Monitor, enabled: boolean) => {
			await updateMonitor({
				data: {
					id: monitor.id,
					is_enabled: enabled,
				},
			});
		},
		[updateMonitor]
	);

	const handleTagChange = useCallback(
		(id: string) => (tags: string[]) => {
			updateMonitor({
				data: {
					id,
					tags,
				},
			});
		},
		[updateMonitor]
	);

	const columns: ExtendedDataTableColumn<T>[] = useMemo(
		() => [
			{
				accessor: 'name',
				titleLabel: 'Monitors',
				render: (record) => <MonitorResourceRenderV2 record={record} />,
			},
			{
				accessor: 'status',
				titleLabel: 'Status',
				render: (record) => (
					<MonitorStatusBadge monitor={record} withAction={false} />
				),
			},
			{
				accessor: 'incident_status',
				titleLabel: 'Incident status',
				render: (record) => (
					<IncidentStatusRender incidentId={record.last_incident} />
				),
			},
			{
				navigate: false,
				accessor: 'tags',
				titleLabel: 'Tags',
				render: (record) => (
					<TagRender
						record={record}
						onChange={handleTagChange}
						recordType="monitor"
						v2Permission="Monitors.Update"
					/>
				),
				width: 200,
			},
			{
				accessor: 'database',
				titleLabel: 'Database',
				filterOptionType: FilterOptionType.DATABASE,
				render: (record) => (
					<Text size="sm" truncate>
						{record.database ?? ''}
					</Text>
				),
			},
			{
				accessor: 'schema',
				titleLabel: 'Schema',
				filterOptionType: FilterOptionType.SCHEMA,
				render: (record) => (
					<Text size="sm" truncate>
						{record.schema ?? ''}
					</Text>
				),
			},
			{
				accessor: 'owners',
				titleLabel: 'Owners',
				filterOptionType: FilterOptionType.OWNERS,
				navigate: false,
				render: (record) => (
					<OptimizedOwnerRender
						record={
							record as Record<string, unknown> & {
								id: string;
								owners?: string[];
								owners_groups?: string[];
							}
						}
					/>
				),
			},
			{
				accessor: 'last_attempted_at',
				titleLabel: 'Last run',
				render: (record: Monitor) => (
					<LastUpdatedRender record={record} field={'last_attempted_at'} />
				),
			},
			{
				accessor: 'estimated_next_run_at',
				titleLabel: 'Next run',
				render: (record: Monitor) => (
					<LastUpdatedRender record={record} field={'estimated_next_run_at'} />
				),
			},
			{
				accessor: 'is_enabled',
				titleLabel: 'State',
				navigate: false,
				render: (record) => (
					<MonitorToggle
						enabled={record.is_enabled ?? false}
						onChange={(enabled) => toggleCallBack(record, enabled)}
						disabled={!record.permissions?.edit}
					/>
				),
				width: 70,
			},
		],
		[toggleCallBack, handleTagChange]
	);

	return columns;
}

export function useBulkEditThresholdsModal() {
	const [bulkEditOpen, setBulkEditOpen] = useState(false);
	const [bulkEditMonitors, setBulkEditMonitors] = useState<Monitor[]>([]);

	const openModal = (monitors: Monitor[]) => {
		setBulkEditMonitors(monitors);
		setBulkEditOpen(true);
	};
	const closeModal = () => {
		setBulkEditOpen(false);
		setBulkEditMonitors([]);
	};

	const modal = (
		<BulkEditThresholdsModal
			key="bulk-edit-thresholds-modal"
			opened={bulkEditOpen}
			onClose={closeModal}
			monitors={bulkEditMonitors}
		/>
	);

	return { openModal, modal };
}

export function useBulkAddTagsModal() {
	const [bulkAddTagsOpen, setBulkAddTagsOpen] = useState(false);
	const [bulkAddTagsMonitors, setBulkAddTagsMonitors] = useState<Monitor[]>([]);

	const openModal = (monitors: Monitor[]) => {
		setBulkAddTagsMonitors(monitors);
		setBulkAddTagsOpen(true);
	};
	const closeModal = () => {
		setBulkAddTagsOpen(false);
		setBulkAddTagsMonitors([]);
	};

	const modal = (
		<BulkAddTagsModal
			key="bulk-add-tags-modal"
			opened={bulkAddTagsOpen}
			onClose={closeModal}
			monitors={bulkAddTagsMonitors}
		/>
	);

	return { openModal, modal };
}

export function useBulkEditSchedulesModal() {
	const [bulkEditSchedulesOpen, setBulkEditSchedulesOpen] = useState(false);
	const [bulkEditSchedulesMonitors, setBulkEditSchedulesMonitors] = useState<
		Monitor[]
	>([]);

	const openModal = (monitors: Monitor[]) => {
		setBulkEditSchedulesMonitors(monitors);
		setBulkEditSchedulesOpen(true);
	};
	const closeModal = () => {
		setBulkEditSchedulesOpen(false);
		setBulkEditSchedulesMonitors([]);
	};

	const modal = (
		<BulkEditSchedulesModal
			key="bulk-edit-schedules-modal"
			opened={bulkEditSchedulesOpen}
			onClose={closeModal}
			monitors={bulkEditSchedulesMonitors}
		/>
	);

	return { openModal, modal };
}

export function openBulkEditOwnersModal(monitors: Monitor[]) {
	closeSpotlight('bulkActions');
	openManagedModal({
		modalId: BULK_EDIT_OWNERS_MODAL_ID,
		withCloseButton: false,
		closeOnEscape: true,
		closeOnClickOutside: true,
		size: 520,
		children: (
			<Suspense fallback={null}>
				<BulkEditOwnersModal
					onClose={() => closeManagedModal(BULK_EDIT_OWNERS_MODAL_ID)}
					monitors={monitors}
				/>
			</Suspense>
		),
	});
}

export const useActions = (
	openBulkEditModal?: (monitors: Monitor[]) => void,
	openBulkAddTagsModal?: (monitors: Monitor[]) => void,
	openBulkEditSchedulesModal?: (monitors: Monitor[]) => void,
	openBulkEditOwnersModal?: (monitors: Monitor[]) => void
): ICommandListItem<Monitor>[] => {
	const { runMonitors } = useRunMonitors();
	const { mutateAsync: deleteMonitor } = useDeleteMonitor({});
	const { mutateAsync: bulkUpdateMonitors } = useBulkUpdateMonitors({});

	const onDelete = useCallback(
		(id: string) => deleteMonitor({ id }),
		[deleteMonitor]
	);
	const { action: deleteManyAction } = useDeleteManyAction<Monitor>({
		name: 'monitor',
		queryKeys: monitorsQueryKeyFactory.allLists(),
		onDelete,
	});

	const handleRunMonitors = useCallback(
		async (entities: Monitor[]) => {
			let monitorIds =
				entities
					?.filter(({ is_enabled: isEnabled }) => isEnabled)
					.map(({ id }) => id) ?? [];

			showNotification({
				title: 'Monitors running',
				message: 'All the selected monitors have been triggered',
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
			});

			await runMonitors(monitorIds);
		},
		[runMonitors]
	);

	const handleToggleMonitor = useCallback(
		async (entities: Monitor[], enable: boolean) => {
			const monitorIds = entities.map(({ id }) => id);

			// Build updates object for bulk update
			const updates: Record<string, Record<string, any>> = {};
			monitorIds.forEach((id) => {
				updates[id] = {
					is_enabled: enable,
				};
			});

			await bulkUpdateMonitors({
				body: {
					updates,
				},
			});

			showNotification({
				title: `Monitor${monitorIds.length > 1 ? 's' : ''} ${
					enable ? 'enabled' : 'paused'
				}`,
				message: `The selected monitor${
					monitorIds.length > 1 ? 's have' : ' has'
				} been ${enable ? 'enabled' : 'paused'}.`,
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
			});
		},
		[bulkUpdateMonitors]
	);

	const handleBulkEditThresholds = useCallback(
		(entities: Monitor[]) => {
			if (openBulkEditModal) openBulkEditModal(entities);
		},
		[openBulkEditModal]
	);

	const handleBulkAddTags = useCallback(
		(entities: Monitor[]) => {
			if (openBulkAddTagsModal) openBulkAddTagsModal(entities);
		},
		[openBulkAddTagsModal]
	);

	const handleBulkEditSchedules = useCallback(
		(entities: Monitor[]) => {
			if (openBulkEditSchedulesModal) openBulkEditSchedulesModal(entities);
		},
		[openBulkEditSchedulesModal]
	);

	const handleBulkEditOwners = useCallback(
		(entities: Monitor[]) => {
			if (openBulkEditOwnersModal) openBulkEditOwnersModal(entities);
		},
		[openBulkEditOwnersModal]
	);

	const actions = useMemo(
		() => [
			{
				id: 'actions::run',
				title: 'Run monitor',
				name: 'Run',
				iconName: 'refresh' as IconNames,
				hotkey: '/mp',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: true,
				onClick: handleRunMonitors,
			},
			{
				id: 'actions::enable',
				title: 'Enable monitor',
				name: 'Enable',
				iconName: 'playerPlay' as IconNames,
				type: EntityType.all,
				category: 'actions',
				show: true,
				onClick: (entities: Monitor[]) => handleToggleMonitor(entities, true),
			},
			{
				id: 'actions::pause',
				title: 'Pause monitor',
				name: 'Pause',
				iconName: 'playerPause' as IconNames,
				type: EntityType.all,
				category: 'actions',
				show: true,
				onClick: (entities: Monitor[]) => handleToggleMonitor(entities, false),
			},
			{
				id: 'actions::change-thresholds',
				title: 'Change thresholds',
				name: 'Change thresholds',
				iconName: 'adjustmentsHorizontal' as IconNames,
				type: EntityType.all,
				category: 'actions',
				show: true,
				onClick: handleBulkEditThresholds,
			},
			{
				id: 'actions::add-tags',
				title: 'Add tags',
				name: 'Add tags',
				iconName: 'tag' as IconNames,
				type: EntityType.all,
				category: 'actions',
				show: true,
				onClick: handleBulkAddTags,
			},
			{
				id: 'actions::change-owners',
				title: 'Change owners',
				name: 'Change owners',
				iconName: 'user' as IconNames,
				type: EntityType.all,
				category: 'actions',
				show: true,
				onClick: handleBulkEditOwners,
			},
			{
				id: 'actions::change-schedules',
				title: 'Change schedules',
				name: 'Change schedules',
				iconName: 'clock' as IconNames,
				type: EntityType.all,
				category: 'actions',
				show: true,
				onClick: handleBulkEditSchedules,
			},
			deleteManyAction,
		],
		[
			deleteManyAction,
			handleRunMonitors,
			handleToggleMonitor,
			handleBulkEditThresholds,
			handleBulkAddTags,
			handleBulkEditSchedules,
			handleBulkEditOwners,
		]
	);

	return actions;
};
